import React, { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PDF } from "@/constants/testIds";
import { Download, Send, CheckCircle2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = `${API}/pdf/definitions`;

export const EmailPdfSection = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/pdf/email`, { email });
      setSentTo(email);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid={PDF.emailRoot}
      id="get-pdf"
      className="py-24 lg:py-32 bg-[#2C352D]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 text-[#F9F6F0]">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-[#A7C2AE]">
              <FileText size={14} /> Get the definitions in your inbox
            </div>
            <h2 className="font-serif-display mt-4 text-4xl sm:text-5xl text-[#F9F6F0] leading-[1.05]">
              Email me the print-ready PDF.
            </h2>
            <p className="mt-5 text-[#D1C9B9] leading-relaxed max-w-xl">
              We'll send the full Vegetarian Food Types Definition — including the
              colour-coded VH&nbsp;/&nbsp;VO&nbsp;/&nbsp;VA&nbsp;/&nbsp;VJ&nbsp;/&nbsp;VV
              table, exceptions and preparation rules — straight to your inbox. Share
              it with your team, kitchen, school or community.
            </p>

            {sentTo ? (
              <div
                data-testid={PDF.emailSuccess}
                className="mt-8 flex items-start gap-3 bg-[#234836] border border-[#658D66] rounded-lg p-5 text-[#F9F6F0]"
              >
                <CheckCircle2 size={20} className="mt-0.5 text-[#A7C2AE] shrink-0" />
                <div>
                  <p className="font-medium">It's on its way to {sentTo}.</p>
                  <p className="text-sm text-[#A7C2AE] mt-1">
                    Please check your inbox (and your spam folder).
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md"
              >
                <Input
                  data-testid={PDF.emailInput}
                  type="email"
                  required
                  placeholder="you@organisation.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#F9F6F0] text-[#2C352D] border-[#658D66] focus-visible:ring-[#A7C2AE]"
                />
                <Button
                  data-testid={PDF.emailSubmit}
                  type="submit"
                  disabled={submitting}
                  className="bg-[#F9F6F0] text-[#2E5C46] hover:bg-white shrink-0"
                >
                  {submitting ? (
                    "Sending…"
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Send size={16} /> Email me the PDF
                    </span>
                  )}
                </Button>
              </form>
            )}
            {error && (
              <p className="mt-3 text-sm text-[#F5C9B9]">{error}</p>
            )}

            <div className="mt-6">
              <a
                href={PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#A7C2AE] hover:text-[#F9F6F0]"
              >
                <Download size={14} /> Or download it directly
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#234836] border border-[#658D66] rounded-lg p-8 text-[#F9F6F0] relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#2E5C46] opacity-40" />
              <div className="relative">
                <FileText size={28} className="text-[#A7C2AE]" />
                <h3 className="font-serif-display text-2xl mt-4">
                  VegUK — Vegetarian Definitions
                </h3>
                <ul className="mt-6 space-y-3 text-sm text-[#D1C9B9]">
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A7C2AE] shrink-0" />
                    All five types — VH, VO, VA, VJ, VV — with definitions
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A7C2AE] shrink-0" />
                    Exceptions: gelatine, isinglass, animal rennet, alcohol
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A7C2AE] shrink-0" />
                    Six preparation & serving requirements
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A7C2AE] shrink-0" />
                    A4 print-ready · single PDF · free
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
